import { createContext } from "react";
import { GameState, setState } from "./store/gameStateSlice";
import { GameMode } from "./Types";
import { DocumentReference } from "firebase/firestore";
import {
  writeAcceptDoubleToDB,
  writeAutomaticDoubleToDB,
  writeEndTurnToDB,
  writeForfeitToDB,
  writeGameOverToDB,
  writeMatchSettingsToDB,
  writeNewGameStartToDB,
  writeNewGameStateToDB,
  writePlayerReadyForNextGameToDB,
} from "./Firebase";
import { clearProvisionalMoves } from "./store/provisionalMovesSlice";
import { clearLastPointClicked } from "./store/lastPointClickedSlice";
import { setCurrentPlayer } from "./store/currentPlayerSlice";
import { rollDice } from "./store/diceSlice";
import { resetForMode, setGameBoardState } from "./store/gameBoardSlice";
import { GameBoardState, Move, Player } from "./Types";
import { genNetworkedMovesID, resetLocalStore } from "./Utils";
import { NetworkedMovesPayload } from "./store/animatableMovesSlice";
import {
  DoublingCubeData,
  InitialDoublingCubeState,
  setDoublingCubeData,
} from "./store/doublingCubeSlice";
import {
  setGameMode,
  setShowGameOverDialog,
  setShowMatchSetupScreen,
} from "./store/settingsSlice";
import {
  MatchScore,
  setMatchScore,
  setPointsRequiredToWin,
} from "./store/matchScoreSlice";
import { setWipeTransition } from "./store/wipeTransitionSlice";
import {
  ReadyForNextGameData,
  setReadyForNextGameData,
} from "./store/readyForNextGameSlice";
import { appendProvisionalMove } from "./store/provisionalMovesSlice";
import { enqueueAnimatableMoves } from "./store/animatableMovesSlice";
import { setBetAmount } from "./store/betSlice";
import { applyMoveToGameBoardState } from "./store/gameBoardSlice";
import { selectBotMoveSet } from "./BotPlayer";

export class Actions {
  /**
   * Certain actions or pieces of UI should only be performed or shown to
   * the "host" client. For a networked game, this will be the player who
   * created the lobby. For a local game, there's only one client which is
   * always the host.
   *
   * @returns true if the current client is the host
   */
  isHostClient(): boolean {
    console.error("Unexpected use of default ActionsContext.");
    return false;
  }

  beginCoinFlip(): void {
    console.error("Unexpected use of default ActionsContext.");
  }

  beginFirstTurn(_startingPlayer: Player): void {
    console.error("Unexpected use of default ActionsContext.");
  }

  async rollButtonClicked(): Promise<void> {
    console.error("Unexpected use of default ActionsContext.");
  }

  async offerDoubleButtonClicked(): Promise<void> {
    console.error("Unexpected use of default ActionsContext.");
  }

  async acceptDoubleButtonClicked(
    _newDoublingCubeOwner: Player,
    _newGameStakes: number
  ): Promise<void> {
    console.error("Unexpected use of default ActionsContext.");
  }

  async automaticDouble(): Promise<void> {
    console.error("Unexpected use of default ActionsContext.");
  }

  async forfeitButtonClicked(
    _newGameBoardState: GameBoardState,
    _newMatchScore: MatchScore
  ): Promise<void> {
    console.error("Unexpected use of default ActionsContext.");
  }

  async submitMoves(
    _newGameBoardState: GameBoardState,
    _newCurrentPlayer: Player,
    _movesToSubmit: Move[]
  ): Promise<void> {
    console.error("Unexpected use of default ActionsContext.");
  }

  async gameOver(
    _newGameBoardState: GameBoardState,
    _winningGameState:
      | GameState.GameOver
      | GameState.GameOverGammon
      | GameState.GameOverBackgammon,
    _newMatchScore: MatchScore,
    _losingPlayer: Player,
    _winningMoves: Move[]
  ): Promise<void> {
    console.error("Unexpected use of default ActionsContext.");
  }

  async updateMatchSettings(
    _matchPoints: number,
    _enableDoubling: boolean,
    _gameMode?: GameMode,
    _betAmount?: number
  ): Promise<void> {
    console.error("Unexpected use of default ActionsContext.");
  }

  async nextGameButtonClicked(
    _newReadyForNextGameData: ReadyForNextGameData
  ): Promise<void> {
    console.error("Unexpected use of default ActionsContext.");
  }

  async bothPlayersReadyForNextGame(): Promise<void> {
    console.error("Unexpected use of default ActionsContext.");
  }

  /**
   * ID партии для серверного settle. Должен быть детерминирован у обоих
   * клиентов одного матча в одной завершающейся партии. null = локальная
   * игра, settle не нужен.
   */
  getCurrentMatchSettleId(_matchScoreBeforeWin: MatchScore): string | null {
    return null;
  }
}

export class LocalGameActions extends Actions {
  dispatchFn: Function;
  gameMode: GameMode;

  constructor(dispatchFn: Function, gameMode: GameMode = GameMode.Backgammon) {
    super();
    this.dispatchFn = dispatchFn;
    this.gameMode = gameMode;
  }

  isHostClient(): boolean {
    return true;
  }

  beginCoinFlip(): void {
    this.dispatchFn(setState(GameState.CoinFlip));
  }

  beginFirstTurn(startingPlayer: Player): void {
    this.dispatchFn(setCurrentPlayer(startingPlayer));
    this.dispatchFn(setState(GameState.PlayerMoving));
    // Если первым ходит бот — запускаем его ход
    if (startingPlayer === Player.Two) {
      const store = (window as any).__BG_STORE__;
      if (store) {
        const state = store.getState();
        this.scheduleBotTurn(state.gameBoard);
      }
    }
  }

  async rollButtonClicked(): Promise<void> {
    this.dispatchFn(setState(GameState.PlayerMoving));
  }

  async offerDoubleButtonClicked(): Promise<void> {
    this.dispatchFn(setState(GameState.PlayerOfferingDouble));
  }

  async acceptDoubleButtonClicked(
    newDoublingCubeOwner: Player,
    newGameStakes: number
  ): Promise<void> {
    this.dispatchFn(
      setDoublingCubeData({
        owner: newDoublingCubeOwner,
        gameStakes: newGameStakes,
        enabled: true,
      })
    );
    this.dispatchFn(setState(GameState.PlayerRolling));
  }

  async automaticDouble(): Promise<void> {
    this.dispatchFn(
      setDoublingCubeData({
        owner: null,
        gameStakes: 2,
        enabled: true,
      })
    );
  }

  async forfeitButtonClicked(
    newGameBoardState: GameBoardState,
    newMatchScore: MatchScore
  ): Promise<void> {
    this.dispatchFn(clearProvisionalMoves());
    this.dispatchFn(clearLastPointClicked());
    this.dispatchFn(setState(GameState.GameOverForfeit));
    this.dispatchFn(setGameBoardState(newGameBoardState));
    this.dispatchFn(setMatchScore(newMatchScore));
    this.dispatchFn(setShowGameOverDialog(true));
  }

  async submitMoves(
    newGameBoardState: GameBoardState,
    newCurrentPlayer: Player,
    _movesToSubmit: Move[]
  ): Promise<void> {
    this.dispatchFn(clearProvisionalMoves());
    this.dispatchFn(clearLastPointClicked());
    this.dispatchFn(setState(GameState.PlayerRolling));
    this.dispatchFn(setGameBoardState(newGameBoardState));
    this.dispatchFn(setCurrentPlayer(newCurrentPlayer));
    this.dispatchFn(rollDice());

    // Если следующий ход — бот (Player.Two), запускаем его автоматически.
    if (newCurrentPlayer === Player.Two) {
      this.scheduleBotTurn(newGameBoardState);
    }
  }

  /** Запланировать ход бота после небольшой задержки (пока игрок видит кубики). */
  private scheduleBotTurn(boardState: GameBoardState): void {
    // Сразу переводим в PlayerMoving чтобы показать кости и анимацию броска.
    this.dispatchFn(setState(GameState.PlayerMoving));
    // Ждём пока закончится анимация броска (700мс) + пауза на реализм (1300мс)
    setTimeout(() => this.playBotTurn(boardState), 2000);
  }

  /** Выполнить ход бота: сделать ходы по одному, сдать. */
  private async playBotTurn(boardState: GameBoardState): Promise<void> {
    const store = (window as any).__BG_STORE__;
    if (!store) return;

    try {
      const state = store.getState();
      const dice: number[] = state.dice.currentRoll ?? [];
      const availableDice =
        dice.length === 2 && dice[0] === dice[1] ? [dice[0], dice[0], dice[0], dice[0]] : [...dice];

      let currentBoard = boardState;
      const allMoves: Move[] = [];
      const remainingDice = [...availableDice];
      // Защита от зацикливания: бот не должен делать больше чем костей.
      const maxIterations = availableDice.length + 2;
      let iterations = 0;

      while (remainingDice.length > 0 && iterations < maxIterations) {
        iterations++;
        const ctx =
          this.gameMode === GameMode.LongNardy
            ? { provisionalMoves: [...allMoves] }
            : undefined;
        const moves = selectBotMoveSet(
          currentBoard,
          remainingDice,
          Player.Two,
          this.gameMode,
          ctx
        );
        if (moves.length === 0) break;
        const m = moves[0];
        allMoves.push(m);
        currentBoard = applyMoveToGameBoardState(currentBoard, m);
        const idx = remainingDice.indexOf(m.dieUsed);
        if (idx >= 0) remainingDice.splice(idx, 1);
      }

      for (const move of allMoves) {
        this.dispatchFn(appendProvisionalMove(move));
        this.dispatchFn(enqueueAnimatableMoves([move]));
        await new Promise((r) => setTimeout(r, 650));
      }

      // Ждём пока очередь анимаций реально опустеет (но не вечно).
      // Раньше после фиксированных 300мс могли повиснуть фишки, и
      // setGameBoardState(currentBoard) накладывался поверх ещё не
      // отыгранного хода — отсюда фантомы.
      const waitForAnimationsDrained = async () => {
        const start = Date.now();
        while (Date.now() - start < 4000) {
          const s = (window as any).__BG_STORE__?.getState();
          if (!s) return;
          if (s.animatableMoves.length === 0) return;
          await new Promise((r) => setTimeout(r, 80));
        }
      };
      await waitForAnimationsDrained();

      this.dispatchFn(clearProvisionalMoves());
      this.dispatchFn(clearLastPointClicked());

      // Если последний ход бота уже привёл к GameOver — не трогаем ни доску,
      // ни currentPlayer. GameBoard.onAnimationComplete уже всё применил.
      const curState = (window as any).__BG_STORE__?.getState().gameState as GameState;
      const finished =
        curState === GameState.GameOver ||
        curState === GameState.GameOverGammon ||
        curState === GameState.GameOverBackgammon ||
        curState === GameState.GameOverForfeit;
      if (finished) return;

      this.dispatchFn(setGameBoardState(currentBoard));
      this.dispatchFn(setState(GameState.PlayerRolling));
      this.dispatchFn(setCurrentPlayer(Player.One));
      this.dispatchFn(rollDice());
    } catch (err) {
      console.error("Bot turn error:", err);
      // Страховка: всё равно возвращаем ход игроку
      this.dispatchFn(clearProvisionalMoves());
      this.dispatchFn(setState(GameState.PlayerRolling));
      this.dispatchFn(setCurrentPlayer(Player.One));
      this.dispatchFn(rollDice());
    }
  }

  async gameOver(
    newGameBoardState: GameBoardState,
    winningGameState:
      | GameState.GameOver
      | GameState.GameOverGammon
      | GameState.GameOverBackgammon,
    newMatchScore: MatchScore,
    _losingPlayer: Player,
    _winningMoves: Move[]
  ): Promise<void> {
    this.dispatchFn(clearProvisionalMoves());
    this.dispatchFn(clearLastPointClicked());
    this.dispatchFn(setState(winningGameState));
    this.dispatchFn(setGameBoardState(newGameBoardState));
    this.dispatchFn(setMatchScore(newMatchScore));
    this.dispatchFn(setShowGameOverDialog(true));
  }

  async updateMatchSettings(
    pointsRequiredToWin: number,
    enableDoubling: boolean,
    gameMode: GameMode = GameMode.Backgammon,
    betAmount: number = 0
  ): Promise<void> {
    this.dispatchFn(setGameMode(gameMode));
    this.dispatchFn(resetForMode(gameMode));
    this.dispatchFn(setPointsRequiredToWin(pointsRequiredToWin));
    this.dispatchFn(setBetAmount(betAmount));
    this.dispatchFn(
      setDoublingCubeData({
        ...InitialDoublingCubeState,
        enabled: enableDoubling,
      })
    );
    this.dispatchFn(setShowMatchSetupScreen(false));
    this.dispatchFn(setWipeTransition(true));
  }

  async nextGameButtonClicked(
    _newReadyForNextGameData: ReadyForNextGameData
  ): Promise<void> {
    resetLocalStore(this.dispatchFn, false, this.gameMode);
    this.dispatchFn(setGameMode(this.gameMode));
    this.dispatchFn(setShowGameOverDialog(false));
    this.dispatchFn(setWipeTransition(true));
  }

  async bothPlayersReadyForNextGame(): Promise<void> {
    console.error("Unexpected use of default ActionsContext.");
  }
}

export class NetworkedGameActions extends Actions {
  isHost: boolean;
  dispatchFn: Function;
  docRef: DocumentReference;
  gameMode: GameMode;

  constructor(
    isHostClient: boolean,
    dispatchFn: Function,
    docRef: DocumentReference,
    gameMode: GameMode = GameMode.Backgammon
  ) {
    super();
    this.isHost = isHostClient;
    this.dispatchFn = dispatchFn;
    this.docRef = docRef;
    this.gameMode = gameMode;
  }

  isHostClient(): boolean {
    return this.isHost;
  }

  beginCoinFlip(): void {
    writeNewGameStateToDB(this.docRef, GameState.CoinFlip);
  }

  beginFirstTurn(_startingPlayer: Player): void {
    // The correct starting player is already written to the DB upon lobby creation
    // so no need to do anything with startingPlayer here.
    writeNewGameStateToDB(this.docRef, GameState.PlayerMoving);
  }

  async offerDoubleButtonClicked(): Promise<void> {
    await writeNewGameStateToDB(this.docRef, GameState.PlayerOfferingDouble);
  }

  async acceptDoubleButtonClicked(
    newDoublingCubeOwner: Player,
    newGameStakes: number
  ): Promise<void> {
    // newDoublingCubeOwner = тот кто ПРИНЯЛ удвоение (новый хозяин куба)
    // offerer = тот кто ПРЕДЛОЖИЛ (использовал своё право удвоения)
    const offerer =
      newDoublingCubeOwner === Player.One ? Player.Two : Player.One;

    const newDoublingCubeData: DoublingCubeData = {
      owner:               newDoublingCubeOwner,
      gameStakes:          newGameStakes,
      enabled:             true,
      // Используем false вместо undefined — Firestore не сохраняет undefined
      playerOneHasDoubled: offerer === Player.One,
      playerTwoHasDoubled: offerer === Player.Two,
    };

    // Optimistically update local client
    this.dispatchFn(setDoublingCubeData(newDoublingCubeData));
    this.dispatchFn(setState(GameState.PlayerRolling));

    return await writeAcceptDoubleToDB(this.docRef, newDoublingCubeData);
  }

  async automaticDouble(): Promise<void> {
    // Optimistically update local client.
    this.dispatchFn(
      setDoublingCubeData({
        owner: null,
        gameStakes: 2,
        enabled: true,
      })
    );

    return await writeAutomaticDoubleToDB(this.docRef);
  }

  async forfeitButtonClicked(
    newGameBoardState: GameBoardState,
    newMatchScore: MatchScore,
    forfeiter?: Player  // явный проигравший (нужен при отказе от удвоения)
  ): Promise<void> {
    this.dispatchFn(clearProvisionalMoves());
    this.dispatchFn(clearLastPointClicked());
    this.dispatchFn(setState(GameState.GameOverForfeit));
    this.dispatchFn(setGameBoardState(newGameBoardState));
    this.dispatchFn(setMatchScore(newMatchScore));
    if (forfeiter !== undefined) {
      this.dispatchFn(setCurrentPlayer(forfeiter));
    }
    this.dispatchFn(setShowGameOverDialog(true));

    return await writeForfeitToDB(
      this.docRef,
      newGameBoardState,
      newMatchScore,
      forfeiter
    );
  }

  async rollButtonClicked(): Promise<void> {
    await writeNewGameStateToDB(this.docRef, GameState.PlayerMoving);
  }

  async submitMoves(
    newGameBoardState: GameBoardState,
    newCurrentPlayer: Player,
    movesToSubmit: Move[]
  ): Promise<void> {
    let networkedMoves: NetworkedMovesPayload = {
      animateFor: newCurrentPlayer,
      id: genNetworkedMovesID(),
      moves: movesToSubmit,
    };

    this.dispatchFn(clearProvisionalMoves());
    this.dispatchFn(clearLastPointClicked());

    // Optimistically write these changes to the local store first so that
    // local client gets a fast update. The same changes will propagate to the
    // other client via the DB write after this.
    this.dispatchFn(setState(GameState.PlayerRolling));
    this.dispatchFn(setGameBoardState(newGameBoardState));
    this.dispatchFn(setCurrentPlayer(newCurrentPlayer));

    return await writeEndTurnToDB(
      this.docRef,
      newGameBoardState,
      newCurrentPlayer,
      networkedMoves
    );
  }

  async gameOver(
    newGameBoardState: GameBoardState,
    winningGameState:
      | GameState.GameOver
      | GameState.GameOverGammon
      | GameState.GameOverBackgammon,
    newMatchScore: MatchScore,
    losingPlayer: Player,
    winningMoves: Move[]
  ): Promise<void> {
    let networkedMoves: NetworkedMovesPayload = {
      animateFor: losingPlayer,
      id: genNetworkedMovesID(),
      moves: winningMoves,
    };

    this.dispatchFn(clearProvisionalMoves());
    this.dispatchFn(clearLastPointClicked());

    // Optimistically write these changes to the local store first so that
    // local client gets a fast update. The same changes will propagate to the
    // other client via the DB write after this.
    this.dispatchFn(setState(winningGameState));
    this.dispatchFn(setGameBoardState(newGameBoardState));
    this.dispatchFn(setMatchScore(newMatchScore));
    this.dispatchFn(setShowGameOverDialog(true));

    return await writeGameOverToDB(
      this.docRef,
      newGameBoardState,
      winningGameState,
      newMatchScore,
      networkedMoves
    );
  }

  async updateMatchSettings(
    pointsRequiredToWin: number,
    enableDoubling: boolean,
    gameMode: GameMode = GameMode.Backgammon,
    betAmount: number = 0
  ): Promise<void> {
    this.dispatchFn(setGameMode(gameMode));
    this.dispatchFn(resetForMode(gameMode));
    this.dispatchFn(setPointsRequiredToWin(pointsRequiredToWin));
    this.dispatchFn(setBetAmount(betAmount));
    this.dispatchFn(
      setDoublingCubeData({
        ...InitialDoublingCubeState,
        enabled: enableDoubling,
      })
    );
    this.dispatchFn(setShowMatchSetupScreen(false));
    this.dispatchFn(setWipeTransition(true));
    this.gameMode = gameMode;

    return await writeMatchSettingsToDB(
      this.docRef,
      pointsRequiredToWin,
      enableDoubling,
      gameMode,
      betAmount
    );
  }

  async nextGameButtonClicked(
    newReadyForNextGameData: ReadyForNextGameData
  ): Promise<void> {
    // Close game over dialog and write to DB that player is ready
    // for the next game.
    this.dispatchFn(setShowGameOverDialog(false));
    this.dispatchFn(setWipeTransition(true));
    this.dispatchFn(setReadyForNextGameData(newReadyForNextGameData));

    return await writePlayerReadyForNextGameToDB(
      this.docRef,
      newReadyForNextGameData
    );
  }

  async bothPlayersReadyForNextGame(): Promise<void> {
    return await writeNewGameStartToDB(this.docRef, this.gameMode);
  }

  // matchId должен быть одинаковым у обоих клиентов в текущей партии
  // и уникальным от партии к партии в одном матче.
  // Берём id комнаты + сумму очков ДО победы (=индекс текущей партии).
  getCurrentMatchSettleId(matchScoreBeforeWin: MatchScore): string | null {
    if (!this.docRef?.id) return null;
    const totalBefore =
      (matchScoreBeforeWin[Player.One] || 0) +
      (matchScoreBeforeWin[Player.Two] || 0);
    return `${this.docRef.id}-game-${totalBefore}`;
  }
}

export const ActionsContext = createContext<Actions>(new Actions());
